<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <div class="col-12 q-mb-md">
            <div class="text-h4 text-weight-medium">
                {{
                    $t(`Details for {assetName}`, {
                        assetName: asset?.name ? asset.name : $t('Asset')
                    })
                }}
            </div>
            <div class="page-description">
                {{ $t('Manage configuration for this asset.') }}
            </div>
        </div>

        <q-form @submit="submit">
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :label="$t('Name')"
                :rules="[
                    val => val !== '' || $t('Enter the name of this asset')
                ]"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                color="accent"
            />
            <q-input
                v-model="form.description"
                @clear="form.description = ''"
                :label="$t('Description')"
                class="q-mb-lg"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                color="accent"
            />
            <q-input
                data-test="max-delay-input"
                v-model="form.max_downtime_delay"
                :class="$q.screen.gt.xs ? 'q-mb-xs' : 'q-mb-lg'"
                :label="$t('Delay Downtime')"
                :hint="
                    $t(
                        'Maximum amount of time after a production stop before an asset is deemed to be in a downtime.'
                    )
                "
                :rules="[
                    val =>
                        val !== null || $t('Enter a delay downtime duration'),
                    val => isPositiveInt(val)
                ]"
                class="q-mb-xs"
                style="width: 100%"
                lazy-rules
                outlined
                color="accent"
            >
                <template v-slot:append>
                    <div style="font-size: 14px">
                        {{ $t('Seconds') }}
                    </div>
                </template>
            </q-input>
            <q-input
                data-test="max-delay-justification-input"
                v-model="form.max_downtime_justification"
                :class="$q.screen.gt.xs ? 'q-mb-xs' : 'q-mb-lg'"
                :label="$t('Delay Downtime Justification')"
                :hint="
                    $t(
                        'Amount of time to wait before a notification is raised once an asset is down.'
                    )
                "
                :rules="[
                    val =>
                        val !== null ||
                        $t('Enter a delay downtime justification duration'),
                    val => isPositiveInt(val)
                ]"
                style="width: 100%"
                lazy-rules
                outlined
                color="accent"
            >
                <template v-slot:append>
                    <div style="font-size: 14px">
                        {{ $t('Seconds') }}
                    </div>
                </template>
            </q-input>

            <q-item tag="label" v-ripple style="border-radius: 4px">
                <q-item-section>
                    <q-item-label>{{
                        $t('Dismiss Downtime Notification')
                    }}</q-item-label>
                    <q-item-label caption>{{
                        $t('Allow operators to dismiss downtime notifications.')
                    }}</q-item-label>
                </q-item-section>
                <q-item-section avatar>
                    <q-toggle
                        color="accent"
                        v-model="form.allow_downtime_dismissal"
                    />
                </q-item-section>
            </q-item>

            <q-input
                data-test="extended-delay-input"
                v-model="form.extended_downtime_delay"
                :class="$q.screen.gt.xs ? 'q-mb-xs' : 'q-mb-lg'"
                :label="$t('Extended Downtime Delay')"
                :hint="
                    $t(
                        'Amount of time to wait before an email/SMS notification is sent once an asset is down.'
                    )
                "
                :rules="[val => isValidExtendedDowntimeDelay(val)]"
                style="width: 100%"
                lazy-rules
                outlined
                color="accent"
            >
                <template v-slot:append>
                    <div style="font-size: 14px">
                        {{ $t('Seconds') }}
                    </div>
                </template>
            </q-input>

            <div class="border-table q-mt-md" v-if="isTrackingQualityEvents">
                <q-item tag="label" style="border-radius: 4px">
                    <q-item-section class="col-10">
                        <q-item-label>
                            {{ $t('Enable Collection of Quality Defects') }}
                        </q-item-label>
                        <q-item-label caption>
                            {{
                                $t(
                                    'Turn on this setting to monitor product rejects based on your defined threshold.'
                                )
                            }}
                        </q-item-label>
                    </q-item-section>
                    <q-item-section class="col-2" avatar>
                        <q-toggle
                            color="accent"
                            id="quality-event-recording-toggle"
                            v-model="form.enable_quality_event_recording"
                        />
                    </q-item-section>
                </q-item>
                <div
                    v-if="form.enable_quality_event_recording"
                    class="row q-pa-sm"
                >
                    <div class="row q-mx-sm">
                        <div class="col-12 q-mb-sm">
                            <span class="text-weight-medium">
                                {{ $t('Reject Notification Threshold') }}
                            </span>
                            <p class="text-caption q-mt-none q-mb-sm text-grey">
                                {{
                                    $t(
                                        'When rejected products exceed the threshold, users will receive a quality alert requiring input. Customize threshold settings to manage alert frequency.'
                                    )
                                }}
                            </p>
                        </div>
                        <div class="col-6">
                            <q-input
                                v-model="form.acceptable_percentage_rejects"
                                :label="$t('Acceptable Reject Percentage')"
                                :rules="[
                                    val =>
                                        isValidAcceptablePercentageRejects(val)
                                ]"
                                data-test="acceptable-reject-input"
                                outlined
                                lazy-rules
                                color="accent"
                            >
                                <template v-slot:append>%</template>
                            </q-input>
                        </div>
                        <div class="col-6">
                            <q-input
                                v-model="form.acceptable_reject_sample_size"
                                :label="$t('Unit Sample Size')"
                                outlined
                                :rules="[
                                    val =>
                                        val !== '' ||
                                        $t(
                                            'Enter the acceptable reject sample size'
                                        ),
                                    val => isPositiveInt(val)
                                ]"
                                data-test="reject-sample-size-input"
                                lazy-rules
                                color="accent"
                                class="q-ml-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <q-table
                :title="$t('Sensors')"
                :rows="sensors || []"
                :columns="table.cols"
                class="q-mt-md border-table"
                row-key="sensor_id"
                flat
                :no-data-label="$t(noSensorConnectedMessage)"
                :hide-pagination="true"
                :hide-bottom="sensors && sensors.length > 0"
            >
                <template v-slot:body="props">
                    <q-tr :props="props">
                        <q-td key="sensor_status" :props="props">
                            <div
                                v-if="!props.row.is_enabled"
                                class="q-mt-xs"
                                style="
                                    color: red;
                                    display: flex;
                                    justify-content: space-between;
                                "
                            >
                                <q-icon
                                    name="fa-light fa-diamond-exclamation"
                                    style="font-size: 24px"
                                />
                                <div
                                    class="text-weight-medium q-ml-xs"
                                    style="
                                        text-align: center;
                                        vertical-align: middle;
                                    "
                                >
                                    {{ $t('Disabled') }}
                                </div>
                            </div>
                        </q-td>
                        <q-td key="sensor_id" :props="props">
                            {{ props.row.sensor_id }}
                        </q-td>
                        <q-td key="sensor_type" :props="props">
                            {{ $t(getSensorLabel(props.row.sensor_type)) }}
                        </q-td>
                        <q-td key="location" :props="props">
                            {{ props.row.sensor_location }}
                        </q-td>
                        <q-td
                            key="online"
                            :props="props"
                            style="font-size: 24px"
                            class="row items-center no-wrap"
                        >
                            <q-icon
                                :color="
                                    DEVICE_STATUS_STYLE[
                                        deviceStatuses[props.row.device][
                                            'status'
                                        ] || 'unknown'
                                    ].color
                                "
                                :name="
                                    DEVICE_STATUS_STYLE[
                                        deviceStatuses[props.row.device][
                                            'status'
                                        ] || 'unknown'
                                    ].name
                                "
                            >
                            </q-icon>
                            <q-btn
                                @click="
                                    throttledGetDeviceStatus(
                                        props.row.device,
                                        true
                                    )
                                "
                                color="accent"
                                class="float-right q-ml-lg"
                                icon="fas fa-redo"
                                :loading="
                                    deviceStatuses[props.row.device][
                                        'status'
                                    ] === DEVICE_STATUS.loading
                                "
                                :disable="
                                    deviceStatuses[props.row.device][
                                        'status'
                                    ] === DEVICE_STATUS.loading
                                "
                                outline
                            >
                                <q-tooltip style="font-size: 14px">
                                    {{ $t('Check device status') }}
                                </q-tooltip>
                            </q-btn>
                            <q-btn
                                @click="loadDeviceDetails(props.row.device)"
                                color="accent"
                                class="float-right q-ml-lg"
                                icon="fa-solid fa-router"
                                :loading="
                                    deviceStatuses[props.row.device][
                                        'status'
                                    ] === DEVICE_STATUS.loading
                                "
                                :disable="
                                    deviceStatuses[props.row.device][
                                        'status'
                                    ] === DEVICE_STATUS.loading
                                "
                                outline
                            >
                                <q-tooltip style="font-size: 14px">
                                    {{ $t('Show device details') }}
                                </q-tooltip>
                            </q-btn>
                        </q-td>
                    </q-tr>
                </template>
            </q-table>
            <div class="border-table q-mt-lg">
                <q-item-section class="q-ma-md">
                    <q-item-label class="q-label">{{
                        $t('Production Speed Coloring')
                    }}</q-item-label>
                    <q-item-label caption>{{
                        $t(
                            'Color the production speed widgets based on how close it is to the expected production speed.'
                        )
                    }}</q-item-label>
                </q-item-section>
                <speed-color-level
                    v-model="form.speed_widget_color_levels"
                    @input="updateSpeedColorLevels"
                />
            </div>

            <div class="form__actions q-mt-md">
                <q-btn
                    id="save-btn"
                    :label="$t('Save')"
                    :disable="$userCannot('edit', 'assets')"
                    class="float-right"
                    type="submit"
                    color="primary"
                />
                <q-btn
                    @click="$router.go(-1)"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm"
                    color="white"
                    text-color="primary"
                    outline
                />
            </div>
        </q-form>
        <!-- Device details dialog -->
        <device-details-dialog
            v-if="showDeviceDetailsDialog"
            :facilityId="facilityId"
            :device="selectedDevice"
            :start="getReportPeriodISOStr().start"
            :end="getReportPeriodISOStr().end"
            @close="showDeviceDetailsDialog = false"
        />
    </div>
</template>

<script>
import { SENSOR_TYPES_TO_LABELS } from '@models/assets.js';
import { DEVICE_STATUS, DEVICE_STATUS_STYLE } from '@models/devices.js';
import { SENSOR_TYPES } from '@models/sensorTypes.js';
import DeviceDetailsDialog from '@pages/DeviceManagement/DeviceDetailsDialog.vue';
import DeviceManager from '@pages/DeviceManagement/deviceManager.js';
import { noSensorConnectedMessage } from '@utils/getMessages';
import { throttle } from 'quasar';
import SpeedColorLevel from '../../components/SpeedColorLevel.vue';
import { mapActions, mapState } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';
import { useSensorsStore } from '@stores/sensors';
import { useAssetsStore } from '@stores/assets';
import { useDevicesStore } from '@stores/devices';

export default {
    name: 'AssetDetail',
    components: { SpeedColorLevel, DeviceDetailsDialog },
    props: {
        asset: {
            type: [Object],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        this.throttledGetDeviceStatus = throttle(
            this.initializeSensorsPanel,
            1000
        );
        await this.initializeSensorsPanel();
        this.initForm();
        // Initialize device manager for the device details dialog
        this.deviceManager = new DeviceManager(this.localTimezone, this.userId);
    },
    activated() {
        this.statusInterval = setInterval(() => {
            this.initializeSensorsPanel();
        }, 30000);
    },
    deactivated() {
        // clear the device status timer
        clearInterval(this.statusInterval);
    },
    data() {
        return {
            // models
            SENSOR_TYPES_TO_LABELS,
            DEVICE_STATUS,
            DEVICE_STATUS_STYLE,
            sensors: [],
            form: {
                name: '',
                description: '',
                max_downtime_delay: null,
                max_downtime_justification: null,
                extended_downtime_delay: null,
                allow_downtime_dismissal: false,
                speed_widget_color_levels: [],
                enable_quality_event_recording: false,
                acceptable_percentage_rejects: 0,
                acceptable_reject_sample_size: 100
            },
            // sensor table
            statusInterval: null,
            deviceStatuses: {},
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'Sensor Status',
                        name: 'sensor_status',
                        label: ''
                    },
                    {
                        align: 'left',
                        field: 'sensor_id',
                        name: 'sensor_id',
                        label: this.$t('Sensor ID')
                    },
                    {
                        align: 'left',
                        field: 'sensor_type',
                        name: 'sensor_type',
                        label: this.$t('Sensor Type')
                    },
                    {
                        align: 'left',
                        field: 'location',
                        name: 'location',
                        label: this.$t('Location')
                    },
                    {
                        align: 'left',
                        field: 'online',
                        name: 'online',
                        label: this.$t('Online')
                    }
                ]
            },
            // device details dialog
            devices: [],
            selectedDevice: null,
            showDeviceDetailsDialog: false,
            reportIntervalDays: 30,
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            noSensorConnectedMessage
        };
    },
    computed: {
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useAuthStore, ['user']),
        ...mapState(useSensorsStore, ['getSensors']),
        /**
         * Server formatted payload.
         * @return {Object} - Server formatted payload of the form.
         */
        payload() {
            return {
                id: this.asset.id,
                facility: this.asset.facility,
                name: this.form.name,
                description: this.form.description,
                max_downtime_delay: this.form.max_downtime_delay,
                max_downtime_justification:
                    this.form.max_downtime_justification,
                extended_downtime_delay:
                    this.form.extended_downtime_delay === ''
                        ? null
                        : this.form.extended_downtime_delay,
                sensor_configuration: this.asset.sensor_configuration,
                dashboard: this.asset.dashboard,
                allow_downtime_dismissal: this.form.allow_downtime_dismissal,
                speed_widget_color_levels: this.form.speed_widget_color_levels,
                uuid: this.asset.uuid,
                enable_quality_event_recording:
                    this.form.enable_quality_event_recording,
                acceptable_percentage_rejects:
                    this.form.acceptable_percentage_rejects,
                acceptable_reject_sample_size:
                    this.form.acceptable_reject_sample_size
            };
        },
        /**
         * Get user id.
         * @return {Number} - User id.
         */
        userId() {
            return this.user.id;
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useSensorsStore, ['loadSensorsAction']),
        ...mapActions(useAssetsStore, ['updateAssetAction']),
        ...mapActions(useDevicesStore, ['getDeviceStatusAction']),
        /**
         * Load device details dialog.
         * @param {Object} device - Device object.
         */
        async loadDeviceDetails(deviceId) {
            this.beginLoading(`${this.$t('Loading device details...')}`);
            try {
                this.devices = [];

                // Get period of 30 days
                const { start, end } = this.getReportPeriodISOStr();

                // Get devices data
                this.devices = await this.deviceManager.getAllDevices(
                    this.facilityId
                );

                // Get Disconnections report data
                const disconnectionsReportData =
                    await this.deviceManager.getDisconnectionsReport(
                        this.facilityId,
                        start,
                        end
                    );

                this.selectedDevice = this.devices.find(device => {
                    return device['id'] === deviceId;
                });

                this.showDeviceDetailsDialog = true;
            } catch (error) {
                console.error('Failed to fetch devices data due to: ', error);
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Get a period of the past 30 days from the current date
         * @return {Object} - An object with start and end dates as String in ISO 8601 format
         */
        getReportPeriodISOStr() {
            // Ending date is the current date
            const end = new Date();
            // Starting date is previous report days excluding the current day
            const start = new Date(
                new Date().setDate(
                    end.getDate() - (this.reportIntervalDays - 1)
                )
            );
            return {
                start: start.toISOString(),
                end: end.toISOString()
            };
        },
        /**
         * Initializes the form with default values or values from the asset object.
         */
        initForm() {
            const {
                name = '',
                description = '',
                max_downtime_delay = null,
                max_downtime_justification = null,
                extended_downtime_delay = null,
                allow_downtime_dismissal = false,
                speed_widget_color_levels = [],
                enable_quality_event_recording = false,
                acceptable_percentage_rejects = 0,
                acceptable_reject_sample_size = 100
            } = this.asset || {};

            this.form = {
                name,
                description,
                max_downtime_delay,
                max_downtime_justification,
                extended_downtime_delay,
                allow_downtime_dismissal,
                speed_widget_color_levels,
                enable_quality_event_recording,
                acceptable_percentage_rejects,
                acceptable_reject_sample_size
            };
        },
        /**
         * Update asset detail.
         */
        async submit() {
            try {
                await this.updateAssetAction(this.payload);
                this.$notifySuccess(this.$t('Asset updated'));
                this.$router.go(-1);
            } catch (error) {
                if (error && 'enable_quality_event_recording' in error) {
                    this.$notifyError(
                        this.$t(
                            'The quality event recording requires at least two sensors connected to the asset.'
                        )
                    );
                } else if (error && 'acceptable_percentage_rejects' in error) {
                    this.$notifyError(
                        this.$t(
                            'The acceptable reject percentage should be less than or equal to 99.99.'
                        )
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to update asset. Please try again later.'
                        ),
                        error
                    );
                }
            }
        },
        /**
         * Validate a string is a positive integer number representation.
         * @return {(boolean|string)} - Returns True if valid, else return error message.
         */
        isPositiveInt(val) {
            // check if string has integer representation and if it's a positive integer
            if (val.toString().match(/^-{0,1}\d+$/) && parseInt(val) > 0) {
                return true;
            } else {
                return this.$t('Enter a positive number with no decimal point');
            }
        },
        /**
         * Validate a string is a positive integer number greater than Extended Downtime delay.
         * @return {(boolean|string)} - Returns True if valid, else return error message.
         */
        isValidExtendedDowntimeDelay(val) {
            // check if string has integer representation and if it's a positive integer
            if (val === null || val === '') {
                return true;
            } else if (
                val.toString().match(/^-{0,1}\d+$/) &&
                parseInt(val) > this.form.max_downtime_justification
            ) {
                return true;
            } else {
                return this.$t(
                    'Extended downtime delay should be greater than delay downtime justification'
                );
            }
        },
        /**
         * Validates if the given value is a valid acceptable percentage of rejects.
         *
         * @param {string|number|null} value - The value to be validated.
         * @returns {boolean|string} - Returns true if the value is between 0 and 99.99,
         * otherwise returns a localized error message.
         */
        isValidAcceptablePercentageRejects(value) {
            if (!value) {
                return this.$t('Enter the acceptable reject percentage');
            } else if (
                value.toString().match(/^\d+(\.\d{1,2})?$/) &&
                parseInt(value) >= 0 &&
                parseInt(value) <= 99.99
            ) {
                return true;
            } else {
                return this.$t('The percentage should be between 0 and 99.99');
            }
        },
        /**
         * Get the status of a device given the device id
         */
        async updateDeviceStatus(deviceId, showLoading) {
            if (showLoading) {
                this.deviceStatuses[deviceId]['status'] = DEVICE_STATUS.loading;
            }
            try {
                const response = await this.getDeviceStatusAction(deviceId);
                this.deviceStatuses[deviceId]['status'] = response.deviceStatus;
                const sensorResponse = await this.loadSensorsAction(
                    this.asset.id
                );
                this.sensors = sensorResponse.sensors;
                // await this.initializeSensors();
            } catch (error) {
                this.$notifyError('Failed to fetch device status data');
                console.error(
                    'Failed to fetch device status data due to: ',
                    error
                );
                this.deviceStatuses[deviceId]['status'] = DEVICE_STATUS.unknown;
            }
        },
        /**
         * Get the status of all devices in the list of sensors
         */
        async refreshDeviceStatuses() {
            for (var deviceId in this.deviceStatuses) {
                this.updateDeviceStatus(deviceId, false);
            }
        },
        /**
         * Get the sensors for the current asset from store if present
         * If not present retrieve from server
         * Set device statuses as unknown
         */
        async initializeSensorsPanel() {
            // Check if asset is available before proceeding
            if (!this.asset || !this.asset.id) {
                console.warn(
                    'Asset not available yet, skipping sensor initialization'
                );
                return;
            }

            this.beginLoading(`${this.$t('Loading sensors...')}`);
            try {
                const response = await this.loadSensorsAction(this.asset.id);
                this.sensors = response.sensors;
                this.initializeDeviceStatuses();
                this.refreshDeviceStatuses();
            } catch (error) {
                this.$notifyError(
                    this.$t('Failed load the list of sensors for this asset')
                );
                console.error(
                    'Failed to fetch list of sensors due to: ',
                    error
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Initialize Device Status
         */
        initializeDeviceStatuses() {
            this.deviceStatuses = {};
            for (const sensor of this.sensors) {
                this.deviceStatuses[sensor.device] = {
                    status: DEVICE_STATUS.unknown
                };
            }
        },
        /**
         * Update speed color levels
         * @param {Array} levels - Array of speed color levels.
         */
        updateSpeedColorLevels(levels) {
            this.form.speed_widget_color_levels = levels;
        },
        /**
         * Get sensor label.
         * @param {String} sensorType - Sensor type.
         * @return {String} - Sensor label.
         */
        getSensorLabel(sensorType) {
            return SENSOR_TYPES[sensorType]['label'];
        }
    }
};
</script>

<style lang="scss" scoped>
.border-table {
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 4px;
}
.q-label {
    font-size: 20px;
}
</style>
